import type { RefreshJob, AuthConfig, HistoryPoint, ManualOfferInput, NotificationEvent, NotificationSettings, ProbeSummary, SearchCandidate, SearchSource, Session, WishlistGame, WishlistSettings } from "./types";

let csrfToken = "";

export class ApiError extends Error {
  constructor(public status: number, message: string, public detail: unknown = null) {
    super(message);
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(csrfToken && options?.method && options.method !== "GET" ? { "X-CSRF-Token": csrfToken } : {}),
      ...options?.headers,
    },
  });
  if (!response.ok) {
    if (response.status === 401 && !path.startsWith("/api/v1/auth/") && path !== "/api/v1/session") window.dispatchEvent(new Event("pt-session-expired"));
    const payload = await response.json().catch(() => null) as { detail?: unknown } | null;
    throw new ApiError(
      response.status,
      `API request failed: ${response.status}`,
      payload?.detail ?? null,
    );
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

async function authenticate(path: string, options?: RequestInit): Promise<Session> {
  const session = await request<Session>(path, options);
  csrfToken = session.csrf_token;
  return session;
}

export const api = {
  authConfig: () => request<AuthConfig>("/api/v1/auth/config"),
  session: () => authenticate("/api/v1/session"),
  activity: () => authenticate("/api/v1/session/activity", { method: "POST" }),
  loginMiniApp: (initData: string) =>
    authenticate("/api/v1/auth/telegram", {
      method: "POST",
      body: JSON.stringify({ init_data: initData }),
    }),
  loginLocalDev: () =>
    authenticate("/api/v1/auth/local", {
      method: "POST",
    }),
  loginLocal: (username: string, password: string) =>
    authenticate("/api/v1/auth/local", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: async () => {
    await request<void>("/api/v1/auth/logout", { method: "POST" });
    csrfToken = "";
  },
  wishlist: () => request<WishlistGame[]>("/api/v1/wishlist"),
  game: (gameId: number) => request<WishlistGame>(`/api/v1/games/${gameId}`),
  history: (gameId: number) =>
    request<HistoryPoint[]>(`/api/v1/games/${gameId}/history`),
  settings: () => request<WishlistSettings>("/api/v1/settings"),
  updateKeyProviders: (providers: WishlistSettings["key_providers"]) => request<WishlistSettings>("/api/v1/settings/key-providers", {
    method: "PUT", body: JSON.stringify({ providers }),
  }),
  updateSettings: (settings: WishlistSettings) => request<WishlistSettings>("/api/v1/settings", {
    method: "PUT",
    body: JSON.stringify({
      ...settings,
      costs: Object.fromEntries(Object.entries(settings.costs).map(([store, value]) => [store, [value.fee_percent, value.fixed_rub]])),
    }),
  }),
  updateSteamSettings: (wishlistEnabled: boolean, libraryEnabled: boolean, syncIntervalHours: number) => request<WishlistSettings>("/api/v1/settings/steam", {
    method: "PUT",
    body: JSON.stringify({ wishlist_enabled: wishlistEnabled, library_enabled: libraryEnabled, sync_interval_hours: syncIntervalHours }),
  }),
  updateAutomationSettings: (schedule: WishlistSettings["schedule"]) => request<WishlistSettings>("/api/v1/settings/automation", {
    method: "PUT",
    body: JSON.stringify(schedule),
  }),
  notifications: () => request<NotificationSettings>("/api/v1/notifications"),
  notificationEvents: (before?: number) => request<{ events: NotificationEvent[]; next_cursor: number | null }>(`/api/v1/notifications/events${before ? `?before_id=${before}` : ""}`),
  notificationEvent: (id: number) => request<NotificationEvent>(`/api/v1/notifications/events/${id}`),
  resolveSteamNotification: (eventId: number, gameId: number, action: "remove" | "keep") => request<{ changed: boolean }>(`/api/v1/notifications/events/${eventId}/steam/${gameId}/${action}`, { method: "POST" }),
  updateNotificationPreferences: (preferences: NotificationSettings["preferences"]) => request<NotificationSettings>("/api/v1/notifications/preferences", {
    method: "PUT",
    body: JSON.stringify(preferences),
  }),
  subscribePush: (subscription: PushSubscriptionJSON, deviceName: string) => request<NotificationSettings>("/api/v1/notifications/subscriptions", {
    method: "POST",
    body: JSON.stringify({ endpoint: subscription.endpoint, keys: subscription.keys, device_name: deviceName }),
  }),
  unsubscribePush: (subscriptionId: number) => request<void>(`/api/v1/notifications/subscriptions/${subscriptionId}`, { method: "DELETE" }),
  testPush: () => request<{ event_id: number }>("/api/v1/notifications/test", { method: "POST" }),
  probes: () => request<ProbeSummary>("/api/v1/probes"),
  runProbes: () => request<ProbeSummary>("/api/v1/probes/run", { method: "POST" }),
  importSteam: (profile: string) => request<{ added: number; existing: number; missing: number; settings: WishlistSettings }>("/api/v1/settings/steam/import", {
    method: "POST",
    body: JSON.stringify({ profile }),
  }),
  syncSteam: () => request<{ added: number; missing: number; library_count: number | null; settings: WishlistSettings }>("/api/v1/settings/steam/sync", { method: "POST" }),
  search: (query: string, source: SearchSource, signal?: AbortSignal, gameId?: number | null) => request<{ results: SearchCandidate[]; failed_sources: string[] }>(`/api/v1/search/all?q=${encodeURIComponent(query)}&source=${source}${gameId != null ? `&game_id=${gameId}` : ""}`, { signal }),
  createGame: (payload: { title: string; store?: string; url?: string; manual_title?: string; manual_price_rub?: number; offer_type?: string; platform?: string | null; selection_token?: string }) =>
    request<{ id: number }>("/api/v1/games", {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify({ ...payload, initialize: true }),
    }),
  updateGame: (gameId: number, title: string, version?: number) => request<{ id: number }>(`/api/v1/games/${gameId}`, { method: "PATCH", headers: version ? { "If-Match": String(version) } : undefined, body: JSON.stringify({ title }) }),
  deleteGame: (gameId: number, version?: number) => request<void>(`/api/v1/games/${gameId}`, { method: "DELETE", headers: version ? { "If-Match": String(version) } : undefined }),
  addStoreOffer: (gameId: number, store: string, url: string, version?: number, selectionToken?: string) => request(`/api/v1/games/${gameId}/offers/store`, { method: "POST", headers: version ? { "If-Match": String(version) } : undefined, body: JSON.stringify({ store, url, initialize: true, selection_token: selectionToken }) }),
  addManualOffer: (gameId: number, payload: ManualOfferInput, version?: number) => request<{ id: number }>(`/api/v1/games/${gameId}/offers/manual`, { method: "POST", headers: version ? { "If-Match": String(version) } : undefined, body: JSON.stringify(payload) }),
  updateManualOffer: (offerId: number, payload: ManualOfferInput, version?: number) => request<{ id: number }>(`/api/v1/offers/${offerId}`, { method: "PUT", headers: version ? { "If-Match": String(version) } : undefined, body: JSON.stringify(payload) }),
  deleteOffer: (offerId: number, version?: number) => request<void>(`/api/v1/offers/${offerId}`, { method: "DELETE", headers: version ? { "If-Match": String(version) } : undefined }),
  alarm: (gameId: number) => request<{ threshold_rub: number | null }>(`/api/v1/games/${gameId}/alarm`),
  updateAlarm: (gameId: number, thresholdRub: number) => request<{ threshold_rub: number }>(`/api/v1/games/${gameId}/alarm`, { method: "PUT", body: JSON.stringify({ threshold_rub: thresholdRub }) }),
  deleteAlarm: (gameId: number) => request<void>(`/api/v1/games/${gameId}/alarm`, { method: "DELETE" }),
  refreshGame: (gameId: number) => request<{ changed: boolean; failed_stores: string[] }>(`/api/v1/games/${gameId}/refresh`, { method: "POST" }),
  startRefreshJob: () => request<RefreshJob>("/api/v1/wishlist/refresh-jobs", { method: "POST" }),
  latestRefreshJob: () => request<RefreshJob | null>("/api/v1/wishlist/refresh-jobs/latest"),
  refreshJob: (id: number) => request<RefreshJob>(`/api/v1/wishlist/refresh-jobs/${id}`),
  retryRefreshFailure: (jobId: number, gameId: number) => request<RefreshJob>(`/api/v1/wishlist/refresh-jobs/${jobId}/retry/${gameId}`, { method: "POST" }),
  refreshWishlist: () => request<{ total: number; changed: number; failed_games: number; failed_stores: string[] }>("/api/v1/wishlist/refresh", { method: "POST" }),
};
